/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useState } from "react";
import { ResultActions } from "@/components/tools/result-actions";

const materials: Record<string, { unit: string; costPerUnit: number; coverageSqFt: number; label: string }> = {
  mulch: { unit: "cu yd", costPerUnit: 35, coverageSqFt: 108, label: "Mulch (3\" deep)" },
  topsoil: { unit: "cu yd", costPerUnit: 30, coverageSqFt: 108, label: "Topsoil (3\" deep)" },
  gravel: { unit: "cu yd", costPerUnit: 45, coverageSqFt: 81, label: "Gravel (4\" deep)" },
  sod: { unit: "sq ft", costPerUnit: 0.45, coverageSqFt: 1, label: "Sod (per sq ft)" },
  pavers: { unit: "sq ft", costPerUnit: 4.5, coverageSqFt: 1, label: "Concrete Pavers" },
  flagstone: { unit: "sq ft", costPerUnit: 8.0, coverageSqFt: 1, label: "Flagstone" },
  retaining_block: { unit: "block", costPerUnit: 3.5, coverageSqFt: 0, label: "Retaining Wall Block" },
  edging: { unit: "lin ft", costPerUnit: 1.5, coverageSqFt: 0, label: "Landscape Edging" },
};

export function LandscapingCalculatorClient() {
  const [items, setItems] = useState<Array<{ material: string; area: string; linearFt: string; qty: string }>>([
    { material: "mulch", area: "", linearFt: "", qty: "" },
  ]);
  const [laborRate, setLaborRate] = useState("55");
  const [crewSize, setCrewSize] = useState("2");
  const [estimatedDays, setEstimatedDays] = useState("1");
  const [equipmentCost, setEquipmentCost] = useState("");

  const rate = parseFloat(laborRate) || 55;
  const crew = parseInt(crewSize) || 2;
  const days = parseFloat(estimatedDays) || 1;
  const ec = equipmentCost;
  const equip = parseFloat(ec) || 0;

  const addItem = () => {
    setItems([...items, { material: "mulch", area: "", linearFt: "", qty: "" }]);
  };

  const updateItem = (index: number, field: string, value: string) => {
    setItems(items.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  const removeItem = (index: number) => {
    if (items.length > 1) setItems(items.filter((_, i) => i !== index));
  };

  // Calculate material costs
  let totalMaterialCost = 0;
  const lineItems = items.map((item) => {
    const mat = materials[item.material];
    if (!mat) return { label: "", quantity: 0, unit: "", cost: 0 };

    let quantity = 0;
    if (mat.coverageSqFt > 0 && mat.coverageSqFt !== 1) {
      // Bulk material (mulch, topsoil, gravel) — area in sq ft → units needed
      const area = parseFloat(item.area) || 0;
      quantity = Math.ceil(area / mat.coverageSqFt);
    } else if (mat.coverageSqFt === 1) {
      // Per sq ft material (sod, pavers)
      quantity = Math.ceil((parseFloat(item.area) || 0) * 1.1); // 10% waste
    } else {
      // Linear or count items (edging, blocks)
      quantity = parseInt(item.qty || item.linearFt) || 0;
    }

    const cost = quantity * mat.costPerUnit;
    totalMaterialCost += cost;
    return { label: mat.label, quantity, unit: mat.unit, cost };
  });

  // Labor: crew × rate × hours (8hr days)
  const laborHours = crew * days * 8;
  const laborCost = laborHours * rate;
  const totalCost = totalMaterialCost + laborCost + equip;

  const hasResult = totalMaterialCost > 0 || laborCost > 0;

  const fmt = (n: number) =>
    n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const inputClass =
    "mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary";

  return (
    <div className="mt-8 rounded-lg card-premium p-6">
      <h3 className="text-lg font-semibold text-foreground mb-4">Materials</h3>
      {items.map((item, index) => {
        const mat = materials[item.material];
        const needsArea = mat && mat.coverageSqFt > 0;
        const needsQty = mat && mat.coverageSqFt === 0;
        return (
          <div key={index} className="grid gap-3 sm:grid-cols-4 mb-3 items-end">
            <div>
              <label className="block text-sm font-medium text-foreground">Material</label>
              <select
                value={item.material}
                onChange={(e) => updateItem(index, "material", e.target.value)}
                className={inputClass}
              >
                {Object.entries(materials).map(([key, mat]) => (
                  <option key={key} value={key}>{mat.label}</option>
                ))}
              </select>
            </div>
            {needsArea && (
              <div>
                <label className="block text-sm font-medium text-foreground">Area (sq ft)</label>
                <input
                  type="number"
                  value={item.area}
                  onChange={(e) => updateItem(index, "area", e.target.value)}
                  placeholder="500"
                  className={inputClass}
                />
              </div>
            )}
            {needsQty && (
              <div>
                <label className="block text-sm font-medium text-foreground">
                  Quantity ({mat.unit})
                </label>
                <input
                  type="number"
                  value={item.qty}
                  onChange={(e) => updateItem(index, "qty", e.target.value)}
                  placeholder="50"
                  className={inputClass}
                />
              </div>
            )}
            <div>
              <button
                onClick={() => removeItem(index)}
                className="rounded-md px-3 py-2 text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
              >
                Remove
              </button>
            </div>
          </div>
        );
      })}
      <button
        onClick={addItem}
        className="mt-2 rounded-md px-4 py-2 text-sm font-medium bg-muted text-foreground hover:bg-muted/80 transition-colors"
      >
        + Add Material
      </button>

      <h3 className="text-lg font-semibold text-foreground mt-8 mb-4">Labor & Equipment</h3>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <label className="block text-sm font-medium text-foreground">Labor Rate ($/hr)</label>
          <input type="number" value={laborRate} onChange={(e) => setLaborRate(e.target.value)} placeholder="55" className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">Crew Size</label>
          <input type="number" value={crewSize} onChange={(e) => setCrewSize(e.target.value)} placeholder="2" className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">Estimated Days</label>
          <input type="number" value={estimatedDays} onChange={(e) => setEstimatedDays(e.target.value)} placeholder="1" className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">Equipment Rental ($)</label>
          <input type="number" value={equipmentCost} onChange={(e) => setEquipmentCost(e.target.value)} placeholder="200" className={inputClass} />
        </div>
      </div>

      {hasResult && (
        <>
          <div className="mt-8 rounded-lg border border-border bg-muted/20 p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Estimate Summary</h3>

            {/* Line items */}
            <div className="space-y-2 mb-4">
              {lineItems.filter((li) => li.cost > 0).map((li, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-foreground">{li.label} ({fmt(li.quantity)} {li.unit})</span>
                  <span className="font-medium text-foreground">${fmt(li.cost)}</span>
                </div>
              ))}
            </div>

            <div className="border-t border-border pt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <div className="text-sm text-muted-foreground">Materials</div>
                <div className="text-xl font-bold text-foreground">${fmt(totalMaterialCost)}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Labor</div>
                <div className="text-xl font-bold text-foreground">${fmt(laborCost)}</div>
                <div className="text-xs text-muted-foreground">{crew} crew x {days} day(s) x 8hrs</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Equipment</div>
                <div className="text-xl font-bold text-foreground">${fmt(equip)}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Total</div>
                <div className="text-2xl font-bold text-secondary">${fmt(totalCost)}</div>
                <div className="text-xs text-muted-foreground">Before markup and overhead</div>
              </div>
            </div>
          </div>
          <ResultActions
            toolSlug="landscaping-calculator"
            inputs={{ laborRate, crewSize, estimatedDays, equipmentCost }}
            outputs={{ totalMaterialCost, laborCost, totalCost }}
          />
        </>
      )}
    </div>
  );
}
